import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@rocicorp/zero/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { LoadingIcon } from "@/components/ui/loading-icon";
import { queries } from "@/lib/queries";
import { createPublicWorkspacesApi } from "@/api/public-workspaces";
import { useApiClient } from "@/api/_client";
import type { PublicWorkspaceMetadata } from "@ano/shared/api/public-workspaces";
import { trackPublicJoinClicked } from "@/lib/analytics-events";
import {
  CommunityIntro,
  CommunityPreviewSection,
  SplashCard,
} from "./PublicWorkspaceSplash.parts";

const PUBLIC_WORKSPACE_LOOKUP_TIMEOUT_MS = 8000;

interface PublicWorkspaceSplashProps {
  slug: string;
  /** Called after a successful auto-join — App should pin scope to the workspace. */
  onJoined: (workspaceId: string) => void;
  /** Called when the user wants to bail back to home. */
  onCancel: () => void;
}

/**
 * Splash shown to a signed-in user who lands on `<slug>.ano.dev` but
 * isn't a member of that workspace. Two policies branch the UI:
 *
 *   - `auto`: single "Join" button. POST /auto-join → onJoined(id).
 *   - `request`: textarea + "Request to join" button. POST /request-join
 *     transitions the splash to a "Waiting for approval" state driven
 *     by the Zero query `workspace_join_requests.mineForWorkspace`.
 *
 * If the lookup returns 404 the slug isn't public (or doesn't exist).
 * Renders a 'No access' card with a 'Back to home' affordance — the
 * existing default state of `use-workspace-scope.ts` (silent fall-
 * through to home) gets a visible signal instead.
 */
export function PublicWorkspaceSplash({
  slug,
  onJoined,
  onCancel,
}: PublicWorkspaceSplashProps) {
  const apiClient = useApiClient();
  const api = useMemo(() => createPublicWorkspacesApi(apiClient), [apiClient]);

  const [meta, setMeta] = useState<PublicWorkspaceMetadata | null | undefined>(
    undefined,
  );
  const [submitting, setSubmitting] = useState(false);
  // Surfaced INLINE: this splash is an early return in App.tsx, mounted BEFORE
  // <Toaster> (which lives in AppDialogs on the main render path). A toast.error
  // here renders into nothing, so a failed join/request looked like a dead
  // button ("does nothing"). Keep the failure visible in the card instead.
  const [actionError, setActionError] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  // After a rejected user clicks "Try again", suppress the rejected
  // branch and re-render the request form. The Zero query still
  // returns the rejected row (it's the latest until they re-submit);
  // the dismiss flag bridges the gap.
  const [retryAfterReject, setRetryAfterReject] = useState(false);
  const [optimisticPending, setOptimisticPending] = useState(false);
  const [lookupAttempt, setLookupAttempt] = useState(0);
  const [lookupError, setLookupError] = useState<"timeout" | "error" | null>(
    null,
  );
  const joinedFiredRef = useRef(false);

  // Initial lookup — runs once per slug.
  useEffect(() => {
    let cancelled = false;
    let settled = false;
    setMeta(undefined);
    setLookupError(null);
    setMessage("");
    setRetryAfterReject(false);
    setOptimisticPending(false);
    setActionError(null);
    joinedFiredRef.current = false;
    const timeout = window.setTimeout(() => {
      if (cancelled || settled) return;
      settled = true;
      setLookupError("timeout");
      setMeta(null);
    }, PUBLIC_WORKSPACE_LOOKUP_TIMEOUT_MS);
    void api
      .lookupBySlug(slug)
      .then((next) => {
        window.clearTimeout(timeout);
        if (cancelled || settled) return;
        settled = true;
        setMeta(next);
      })
      .catch((err) => {
        window.clearTimeout(timeout);
        if (cancelled || settled) return;
        settled = true;
        if (err instanceof Error) {
          toast.error(err.message || "Could not load workspace");
        }
        setLookupError("error");
        setMeta(null);
      });
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [api, slug, lookupAttempt]);

  // Zero query — own request status once meta is known. Re-renders
  // automatically when admin approves/rejects.
  const [myRequest] = useQuery(
    meta?.workspace_id
      ? queries.workspace_join_requests.mineForWorkspace(meta.workspace_id)
      : null,
  );

  // Auto-redirect when an admin approves a pending request. Guard
  // against re-firing: this effect runs whenever its deps shift
  // identity (including a fresh inline `onJoined` from the App
  // re-render that `onJoined` itself triggers). Without the ref,
  // approval drives a render loop until workspace_members syncs
  // through Zero. Tick the ref once we've fired and never re-call.
  useEffect(() => {
    if (!meta) return;
    if (joinedFiredRef.current) return;
    if (myRequest?.status === "approved") {
      joinedFiredRef.current = true;
      onJoined(meta.workspace_id);
    }
  }, [meta, myRequest?.status, onJoined]);

  const handleJoin = useCallback(async () => {
    if (!meta) return;
    trackPublicJoinClicked({
      slug,
      workspaceId: meta.workspace_id,
      policy: "auto",
    });
    setActionError(null);
    setSubmitting(true);
    try {
      const result = await api.autoJoin(slug);
      onJoined(result.workspace_id);
    } catch (err) {
      setActionError(
        err instanceof Error && err.message
          ? err.message
          : "Could not join — please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }, [api, meta, onJoined, slug]);

  const handleRequest = useCallback(async () => {
    if (!meta) return;
    trackPublicJoinClicked({
      slug,
      workspaceId: meta.workspace_id,
      policy: "request",
    });
    setActionError(null);
    setSubmitting(true);
    try {
      const result = await api.requestJoin(slug, {
        message: message.trim() || undefined,
      });
      if (result.status === "approved") {
        onJoined(meta.workspace_id);
      } else {
        setOptimisticPending(true);
        toast.success("Your request has been sent");
      }
    } catch (err) {
      setActionError(
        err instanceof Error && err.message
          ? err.message
          : "Could not send request — please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }, [api, meta, message, onJoined, slug]);

  if (meta === undefined) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <LoadingIcon size={24} className="text-foreground" />
      </div>
    );
  }

  if (meta === null) {
    // Distinct local name so it never reads as the request-note `message`
    // state above — the two never coexist (this branch returns first), but the
    // shadow is a foot-gun for the next reader.
    const lookupMessage =
      lookupError === "timeout"
        ? "This workspace is taking too long to load."
        : "This workspace doesn’t exist or isn’t open to the public.";
    return (
      <SplashCard slug={slug}>
        <p className="text-sm text-muted-foreground">{lookupMessage}</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            Back to home
          </Button>
          {lookupError === "timeout" && (
            <Button
              onClick={() => setLookupAttempt((n) => n + 1)}
              className="flex-1"
            >
              Try again
            </Button>
          )}
        </div>
      </SplashCard>
    );
  }

  if (meta.public_join_policy === "auto") {
    return (
      <SplashCard slug={slug} meta={meta}>
        <CommunityIntro meta={meta} />
        {meta.entry_mode === "preview_then_join" && (
          <CommunityPreviewSection slug={slug} name={meta.name} api={api} />
        )}
        <p className="text-sm text-muted-foreground">
          Anyone with the link can join{" "}
          <span className="font-medium text-foreground">{meta.name}</span>.
        </p>
        {actionError && (
          <p role="alert" className="text-sm text-destructive">
            {actionError}
          </p>
        )}
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleJoin()}
            disabled={submitting}
            className="flex-1"
          >
            {submitting ? "Joining…" : `Join ${meta.name}`}
          </Button>
        </div>
      </SplashCard>
    );
  }

  if (myRequest?.status === "pending" || optimisticPending) {
    return (
      <SplashCard slug={slug} meta={meta}>
        <p className="text-sm text-muted-foreground">
          Waiting for an admin to approve your request to join{" "}
          <span className="font-medium text-foreground">{meta.name}</span>.
        </p>
        <Button variant="outline" onClick={onCancel}>
          Back to home
        </Button>
      </SplashCard>
    );
  }

  if (myRequest?.status === "rejected" && !retryAfterReject) {
    return (
      <SplashCard slug={slug} meta={meta}>
        <p className="text-sm text-muted-foreground">
          Your previous request to join{" "}
          <span className="font-medium text-foreground">{meta.name}</span> was
          declined.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            Back to home
          </Button>
          <Button onClick={() => setRetryAfterReject(true)} className="flex-1">
            Try again
          </Button>
        </div>
      </SplashCard>
    );
  }

  return (
    <SplashCard slug={slug} meta={meta}>
      <CommunityIntro meta={meta} />
      {meta.entry_mode === "preview_then_join" && (
        <CommunityPreviewSection slug={slug} name={meta.name} api={api} />
      )}
      <p className="text-sm text-muted-foreground">
        Send an admin of{" "}
        <span className="font-medium text-foreground">{meta.name}</span> a
        request to join. They’ll see it in their inbox.
      </p>
      <Textarea
        id="public-workspace-request-message"
        name="join-request-message"
        autoComplete="off"
        aria-label="Message to admins"
        aria-describedby="public-workspace-request-message-help"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Add a short note (optional)…"
        rows={3}
        maxLength={500}
      />
      <p id="public-workspace-request-message-help" className="sr-only">
        Optional message shown to workspace admins. Maximum 500 characters.
      </p>
      {actionError && (
        <p role="alert" className="text-sm text-destructive">
          {actionError}
        </p>
      )}
      <div className="flex gap-2">
        <Button variant="outline" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleRequest()}
          disabled={submitting}
          className="flex-1"
        >
          {submitting ? "Sending…" : "Request to join"}
        </Button>
      </div>
    </SplashCard>
  );
}
